
import { Discount, PromoCodeData } from '@/types/promotions';
import TextField from '@/ui-components/TextField/TextField.vue';
import IButton from '@/ui-components/IButton/IButton.vue';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';
import namespaces from '@/store/namespaces';
import { Tuple } from '@/types/misc';
import { SetPromoCode } from '@/store/modules/campaign-wizard/types';

@Component({
  name: 'PromoCodeInput',
  components: {
    TextField,
    IButton,
  },
})
export default class PromoCodeInput extends Vue {
  @Prop({ type: Boolean })
  public loading!: boolean;
  @Prop({ type: Boolean })
  public disabled!: boolean;

  public showTooltip = false;

  @Getter('discounts', { namespace: namespaces.CampaignWizardModule })
  public discounts!: Discount[];

  @Getter('defaultVsPromoCodeValues', { namespace: namespaces.CampaignWizardModule })
  public defaultVsPromoCodeValues!: Tuple<Discount['discountValue'], Discount['discountValue']> | undefined;
  @Getter('promoCode', { namespace: namespaces.CampaignWizardModule })
  public promoCode!: PromoCodeData;
  @Mutation('setPromoCode', { namespace: namespaces.CampaignWizardModule })
  public setPromoCode!: SetPromoCode;

  public get isApplied() {
    return this.promoCode.status === 'APPLIED';
  }
  
  public get promoCodeInput(): string {
    return this.promoCode.code || '';
  }
  
  public set promoCodeInput(value: string) {
    this.setPromoCode({
      ...this.promoCode,
      code: value,
    });
  }
  get promoCodeIndicatorActive(): boolean {
    return this.isApplied || Boolean(this.hasError);
  }
  public get hasError() {
    return this.promoCode.error;
  }
  get isTextFieldDisabled(): boolean {
    return this.loading || this.disabled || (this.isApplied && !this.defaultVsPromoCodeValues);
  }
  public get errorMessage() {
    const { error = '' } = this.promoCode;
    return error ? this.$t(`errors.${ error }`) : '';
  }

  get tooltipState(): 'error' | 'info' | 'success' | 'idle' {
    if (this.promoCode.error) {
      return 'error';
    }
    if (this.defaultVsPromoCodeValues) {
      return 'info';
    }
    if (this.isApplied) {
      return 'success';
    }
    return 'idle'; // Or simply hide the tooltip when in idle state
  }

// Create a configuration object for cleaner mapping
private get tooltipConfig() {
  return {
    error: {
      icon: 'mdi-exclamation-thick',
      color: 'error' as const,
    },
    info: {
      icon: 'mdi-information-symbol',
      color: 'info' as const,
    },
    success: {
      icon: 'mdi-check',
      color: 'success' as const,
    },
    idle: {
      icon: '',
      color: '' as const,
    }
  };
}

get tooltipIcon() {
  return this.tooltipConfig[this.tooltipState].icon;
}

get tooltipColor() {
  return this.tooltipConfig[this.tooltipState].color;
}

get tooltipMessage() {
  const messageMap = {
    info: () => this.defaultPromotionBetterThanPromoCode(this.defaultVsPromoCodeValues!),
    error: () => this.errorMessage,
    success: () => this.getPromoCodeDiscountReason(),
    idle: () => '',
  };

  return messageMap[this.tooltipState]();
}

private getPromoCodeDiscountReason() {
  const promoCodeDiscount = this.discounts.find(discount => discount?.code === this.promoCode.code?.toLowerCase());
  return promoCodeDiscount?.discountReason?.[this.$i18n.locale];
}

private defaultPromotionBetterThanPromoCode([ defaultValue, promoCodeValue ]: Tuple<Discount['discountValue'], Discount['discountValue']>) {
  return this.$t('default_promo_better_than_promo_code', { defaultValue, promoCodeValue }) as string;
}

  @Emit('changed')
  public remove() {
    this.showTooltip = false;
    this.setPromoCode({
      code: '',
      error: '',
      status: undefined,
    })
  }

  @Emit('changed')
  public apply() {
    this.showTooltip = false;
    this.setPromoCode({
      ...this.promoCode,
      error: '',
      status: undefined,
    })
    return true;
  }


  @Watch('promoCode.status')
  promoCodeStatusChanged(newStatus: PromoCodeData['status']) {
    if (newStatus) {
      this.showTooltip = true;
    }
  }
}
